import type { LucideIcon } from 'lucide-react';
import { Circle, Ban, Loader2, Check, AlertCircle } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../ui/tooltip';
import type { AgentTask, AgentTaskStatus } from '../../../types/agent-tasks';
import { cleanDisplayText } from '@/utils/display-text';

const STATUS_CONFIG: Record<AgentTaskStatus, {
  icon: LucideIcon;
  color: string;
  animate?: string;
}> = {
  pending: { icon: Circle, color: 'text-status-neutral-fg' },
  blocked: { icon: Ban, color: 'text-status-warning-fg' },
  running: { icon: Loader2, color: 'text-status-info-fg', animate: 'animate-spin' },
  completed: { icon: Check, color: 'text-status-success-fg' },
  failed: { icon: AlertCircle, color: 'text-status-error-fg' },
};

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

interface AgentTaskItemProps {
  task: AgentTask;
  allTasks: Map<string, AgentTask>;
  childrenMap: Map<string | undefined, AgentTask[]>;
  depth: number;
}

export function AgentTaskItem({ task, allTasks, childrenMap, depth }: AgentTaskItemProps) {
  const config = STATUS_CONFIG[task.status];
  const Icon = config.icon;
  const children = childrenMap.get(task.taskId);

  const statusIcon = (
    <Icon
      size={14}
      className={`shrink-0 ${config.color} ${config.animate ?? ''}`}
    />
  );

  const blockerTitles = task.status === 'blocked' && task.blockedBy?.length
    ? task.blockedBy
        .map((id) => allTasks.get(id)?.title ?? id)
        .join(', ')
    : null;

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/50 transition-colors"
        style={{ paddingLeft: `${depth * 1.5}rem` }}
      >
        {/* Status icon with tooltip for blocked/failed */}
        {task.status === 'failed' && task.error ? (
          <Tooltip>
            <TooltipTrigger asChild><span>{statusIcon}</span></TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs">{task.error}</p>
            </TooltipContent>
          </Tooltip>
        ) : task.status === 'blocked' && blockerTitles ? (
          <Tooltip>
            <TooltipTrigger asChild><span>{statusIcon}</span></TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="text-xs">Blocked by: {blockerTitles}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          statusIcon
        )}

        {/* Agent name badge for sub-agent tasks */}
        {task.agentName && depth > 0 && (
          <span className="text-xs text-muted-foreground shrink-0">{task.agentName}</span>
        )}

        {/* Title */}
        <span className="text-sm truncate">{cleanDisplayText(task.title)}</span>

        {/* Running: activeForm subtitle */}
        {task.status === 'running' && task.activeForm && (
          <span className="text-xs text-muted-foreground italic truncate ml-1">
            {task.activeForm}
          </span>
        )}

        {/* Completed: duration */}
        {task.status === 'completed' && task.durationMs != null && (
          <span className="text-xs text-muted-foreground ml-auto shrink-0">
            {formatDuration(task.durationMs)}
          </span>
        )}
      </div>

      {/* Recursive children */}
      {children?.map((child) => (
        <AgentTaskItem
          key={child.taskId}
          task={child}
          allTasks={allTasks}
          childrenMap={childrenMap}
          depth={depth + 1}
        />
      ))}
    </div>
  );
}
